import { printStopsPx } from '~/client/src/shared/map/exportAdjustGroups'
import {
  READ_SCALE_BY_SHEET,
  readScaleRungs,
} from '~/client/src/shared/map/exportReadScale'
import {
  EXPORT_SYMBOL_KIND_ORDER,
  EXPORT_TEXT_KIND_ORDER,
  ExportPrintRow,
  ExportPrintTextKind,
  ExportSymbolKind,
  ExportTextKind,
  printAutoPx,
} from '~/client/src/shared/map/exportTextScale'
import {
  EXPORT_PAPER_PRESETS,
  EXPORT_VIEW_PRESETS,
  ExportOrientation,
  ExportPaperPreset,
  ExportQuality,
  ExportScreenShape,
  ExportViewPreset,
  SCREEN_SHAPES,
} from '~/client/src/shared/map/mapExportPaper'
import {
  safeGetJson,
  safeRemoveItem,
  safeSetItem,
} from '~/client/src/shared/utils/safeStorage'

export enum ExportFormat {
  Png = 'png',
  Pdf = 'pdf',
}

export enum ExportSizeKind {
  Screen = 'screen',
  Paper = 'paper',
}

export enum ExportLabelMode {
  Readable = 'readable',
  AsOnScreen = 'asOnScreen',
}

export type ExportKeyPlace = 'besideMap' | 'besidePage' | 'pageTwo'

// What the file is for: a print, sized by the distance it is read from, or a screen image.
export type ExportUseFor = 'print' | 'screen'

export const USE_FOR_ORDER: ExportUseFor[] = ['print', 'screen']

// Screen's Overall size.
export const SIZE_PERCENT_RANGE = { min: 75, max: 150, step: 5 }
export const SIZE_NOTCHES = [75, 100, 125, 150]
export const OVERALL_DEFAULT_PERCENT = 100

// Each row of Adjust by type, text and pins or icons alike.
export const KIND_PERCENT_RANGE = { min: 50, max: 200, step: 5 }
export const KIND_NOTCHES = [50, 100, 150, 200]

// The project's own office paper: Screen's paper for the key's place.
export const defaultPaperFor = (isMetricProject: boolean): ExportPaperPreset =>
  isMetricProject ? 'a4' : 'letter'

// Where Print starts: the full-size construction drawing sheet.
export const defaultPrintPaperFor = (
  isMetricProject: boolean,
): ExportPaperPreset => (isMetricProject ? 'a1' : 'archD')

// The choices a user exported with for a project, remembered in this browser only. Per-view state
// (basemap, layers, framing, angle) is not here: it seeds from the live Map View each time.
export interface IMapExportSettings {
  format: ExportFormat
  useFor: ExportUseFor
  viewPreset: ExportViewPreset
  // Null until a paper is picked, so the project's units choose it.
  paper: ExportPaperPreset | null
  orientation: ExportOrientation
  // Screen's own shape and orientation: a portrait print leaves the next Screen export landscape.
  screenShape: ExportScreenShape
  screenOrientation: ExportOrientation
  quality: ExportQuality
  arePageElementsShown: boolean
  labelMode: ExportLabelMode
  // Print's reading scale, one of the rungs, and Screen's Overall size: each kept across a Use for
  // flip.
  readScale: number
  screenPercent: number
  isKeyIncluded: boolean
  // Screen's rows, and Markup text on both.
  textKindPercent: Record<ExportTextKind, number>
  symbolKindPercent: Record<ExportSymbolKind, number>
  // Print's set sizes in sheet px; a kind left out follows the reading distance (Auto).
  printTextKindPx: Partial<Record<ExportPrintTextKind, number>>
  printSymbolKindPx: Partial<Record<ExportSymbolKind, number>>
}

// ARCH D and A1 read alike, so a paper left to the project's units reads as either.
const readScalePaper = (paper: ExportPaperPreset | null): ExportPaperPreset =>
  paper ?? defaultPrintPaperFor(false)

export const DEFAULT_EXPORT_SETTINGS: IMapExportSettings = {
  format: ExportFormat.Pdf,
  useFor: 'print',
  viewPreset: 'view2',
  paper: null,
  orientation: ExportOrientation.Landscape,
  screenShape: '16x9',
  screenOrientation: ExportOrientation.Landscape,
  quality: ExportQuality.Standard,
  arePageElementsShown: true,
  labelMode: ExportLabelMode.Readable,
  readScale: READ_SCALE_BY_SHEET[readScalePaper(null)].standard,
  screenPercent: OVERALL_DEFAULT_PERCENT,
  isKeyIncluded: true,
  textKindPercent: {
    workflowPin: 100,
    location: 100,
    markup: 100,
    markupText: 100,
    key: 100,
    band: 100,
  },
  symbolKindPercent: {
    workflowPin: 100,
    location: 100,
  },
  printTextKindPx: {},
  printSymbolKindPx: {},
}

// The phone has no Use for: it always makes a Screen file.
export const PHONE_EXPORT_SETTINGS: IMapExportSettings = {
  ...DEFAULT_EXPORT_SETTINGS,
  useFor: 'screen',
}

const QUALITIES = [ExportQuality.Standard, ExportQuality.High]

export const exportSettingsKey = (projectId: string): string =>
  `struxhub:mapExport:v2:${projectId}`

// Where the Made for model kept a project's settings; it is not read any more.
const legacyExportSettingsKey = (projectId: string): string =>
  `struxhub:mapExport:${projectId}`

const oneOf = <T>(allowed: readonly T[], value: unknown, fallback: T): T =>
  (allowed as readonly unknown[]).includes(value) ? (value as T) : fallback

const flag = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback

const isPercentIn = (
  { min, max, step }: typeof SIZE_PERCENT_RANGE,
  value: unknown,
): value is number =>
  typeof value === 'number' &&
  value >= min &&
  value <= max &&
  (value - min) % step === 0

const fieldsOf = <K extends string>(
  value: unknown,
): Partial<Record<K, unknown>> =>
  value && typeof value === 'object' ? value : {}

// A rung within the paper's range, else the paper's Standard.
const parseReadScale = (
  paper: ExportPaperPreset | null,
  value: unknown,
): number =>
  readScaleRungs(readScalePaper(paper)).includes(value as number)
    ? (value as number)
    : READ_SCALE_BY_SHEET[readScalePaper(paper)].standard

function parseKindPercents<K extends string>(
  order: K[],
  range: typeof SIZE_PERCENT_RANGE,
  defaults: Record<K, number>,
  value: unknown,
): Record<K, number> {
  const stored = fieldsOf<K>(value)
  const percents = { ...defaults }
  order.forEach(kind => {
    const percent = stored[kind]
    if (isPercentIn(range, percent)) {
      percents[kind] = percent
    }
  })
  return percents
}

const PRINT_TEXT_KINDS = EXPORT_TEXT_KIND_ORDER.filter(
  (kind): kind is ExportPrintTextKind => kind !== 'markupText',
)

// The widest reach any paper gives a row, with room for a length set in the other unit.
const printReach = (row: ExportPrintRow): { min: number; max: number } => {
  const stops = printStopsPx(row, 'a0')
  return { min: stops[0] * 0.95, max: stops[stops.length - 1] * 1.05 }
}

const isPrintPxIn = (row: ExportPrintRow, px: unknown): px is number =>
  typeof px === 'number' &&
  px >= printReach(row).min &&
  px <= printReach(row).max

// A record saved before Print sizes could be set has none: each row it moved keeps the length it
// printed at then, at its reading distance; a length past the row's reach is held at its end.
function parsePrintPx<K extends ExportPrintTextKind | ExportSymbolKind>(
  kinds: K[],
  rowOf: (kind: K) => ExportPrintRow,
  value: unknown,
  before: { readScale: number; percents: Record<K, number> },
): Partial<Record<K, number>> {
  const sizes: Partial<Record<K, number>> = {}
  if (value === undefined) {
    kinds.forEach(kind => {
      const percent = before.percents[kind]
      if (percent !== 100) {
        const { min, max } = printReach(rowOf(kind))
        sizes[kind] = Math.min(
          max,
          Math.max(
            min,
            (printAutoPx(rowOf(kind), before.readScale) * percent) / 100,
          ),
        )
      }
    })
    return sizes
  }
  const stored = fieldsOf<K>(value)
  kinds.forEach(kind => {
    const px = stored[kind]
    if (isPrintPxIn(rowOf(kind), px)) {
      sizes[kind] = px
    }
  })
  return sizes
}

// Field by field, so one stale or hand-edited value falls back alone.
export function parseExportSettings(raw: unknown): IMapExportSettings {
  const stored = fieldsOf<keyof IMapExportSettings>(raw)
  const fallback = DEFAULT_EXPORT_SETTINGS
  const paper = oneOf<ExportPaperPreset | null>(
    EXPORT_PAPER_PRESETS,
    stored.paper,
    fallback.paper,
  )
  // A record from before Print sizes could be set read a paper left to the project's units as its
  // office paper; Print now starts on the drawing sheet, which its distance was not chosen for.
  const printedReadScale = parseReadScale(paper, stored.readScale)
  const readScale =
    stored.printTextKindPx === undefined && paper === null
      ? parseReadScale(paper, undefined)
      : printedReadScale
  const textKindPercent = parseKindPercents(
    EXPORT_TEXT_KIND_ORDER,
    KIND_PERCENT_RANGE,
    fallback.textKindPercent,
    stored.textKindPercent,
  )
  const symbolKindPercent = parseKindPercents(
    EXPORT_SYMBOL_KIND_ORDER,
    KIND_PERCENT_RANGE,
    fallback.symbolKindPercent,
    stored.symbolKindPercent,
  )
  return {
    format: oneOf(Object.values(ExportFormat), stored.format, fallback.format),
    useFor: oneOf(USE_FOR_ORDER, stored.useFor, fallback.useFor),
    viewPreset: oneOf(
      EXPORT_VIEW_PRESETS,
      stored.viewPreset,
      fallback.viewPreset,
    ),
    paper,
    orientation: oneOf(
      Object.values(ExportOrientation),
      stored.orientation,
      fallback.orientation,
    ),
    screenShape: oneOf(SCREEN_SHAPES, stored.screenShape, fallback.screenShape),
    screenOrientation: oneOf(
      Object.values(ExportOrientation),
      stored.screenOrientation,
      fallback.screenOrientation,
    ),
    quality: oneOf(QUALITIES, stored.quality, fallback.quality),
    arePageElementsShown: flag(
      stored.arePageElementsShown,
      fallback.arePageElementsShown,
    ),
    labelMode: oneOf(
      Object.values(ExportLabelMode),
      stored.labelMode,
      fallback.labelMode,
    ),
    readScale,
    screenPercent: isPercentIn(SIZE_PERCENT_RANGE, stored.screenPercent)
      ? stored.screenPercent
      : fallback.screenPercent,
    isKeyIncluded: flag(stored.isKeyIncluded, fallback.isKeyIncluded),
    textKindPercent,
    symbolKindPercent,
    printTextKindPx: parsePrintPx(
      PRINT_TEXT_KINDS,
      kind => ({ type: 'text', kind }),
      stored.printTextKindPx,
      { readScale: printedReadScale, percents: textKindPercent },
    ),
    printSymbolKindPx: parsePrintPx(
      EXPORT_SYMBOL_KIND_ORDER,
      kind => ({ type: 'symbol', kind }),
      stored.printSymbolKindPx,
      { readScale: printedReadScale, percents: symbolKindPercent },
    ),
  }
}

// Null when nothing could be read, so a session keeps the choices it already holds.
export const loadExportSettings = (
  projectId: string,
): IMapExportSettings | null => {
  const stored = safeGetJson<unknown>(exportSettingsKey(projectId))
  return stored === null ? null : parseExportSettings(stored)
}

export const saveExportSettings = (
  projectId: string,
  settings: IMapExportSettings,
): void => {
  safeSetItem(exportSettingsKey(projectId), JSON.stringify(settings))
  safeRemoveItem(legacyExportSettingsKey(projectId))
}
